import { ReactNode } from 'react'
import { Chip } from '@mui/material'
import { InfoBlueBorderIcon } from '../../icon'
import ButtonComponent from '../shared/button/button.component'

export type ImportFieldKey =
    | "FirstName"
    | "LastName"
    | "Email"
    | "Role"
    | "SeatType"
    | "PhoneNumber"
    | "SecondaryPhoneNumbers"
    | "PhoneNumberGroups"

export interface ImportEditableRow {
    RowId: string
    rowIndex: number
    FirstName: string
    LastName: string
    Email: string
    Role: string
    SeatType?: string
    PhoneNumberGroups?: string
    PhoneNumber?: string
    PrimaryPhoneNumber: string
    SecondaryPhoneNumbers?: string
}

export const USER_IMPORT_COLUMNS = [
    { letter: "A", label: "First Name", required: true },
    { letter: "B", label: "Last Name", required: true },
    { letter: "C", label: "Email", required: true },
    { letter: "D", label: "Role", required: true },
    { letter: "E", label: "Seat Type", required: true },
    { letter: "F", label: "Primary Phone Number", required: true },
    { letter: "G", label: "Secondary Phone Number", required: false }
]

export const IMPORT_ROLE_OPTIONS = ["Org Admin", "Supervisor", "Agent", "Read Only"]

export const FILE_COLUMNS: Record<string, ImportFieldKey> = {
    "first name": "FirstName",
    "lastname": "LastName",
    "last name": "LastName",
    "email": "Email",
    "role": "Role",
    "seat type": "SeatType",
    "line type": "PhoneNumberGroups",
    "primary phone number": "PhoneNumber",
    "secondary phone number": "SecondaryPhoneNumbers"
}

export const normalizeHeader = (value: string) =>
    value.replace(/\*/g, "").replace(/^[A-H]\s*[·.:-]?\s+/i, "").replace(/\s+/g, " ").trim().toLowerCase()

export const importRowsFromFile = (rows: Record<string, string>[]): ImportEditableRow[] => {
    return rows.map((row, index) => {
        
        if(row.Role) {
            row.Role = row.Role.toLowerCase();
            if(row.Role.toLowerCase() === "OrgAdmin".toLowerCase() || row.Role.toLowerCase() === "Admin".toLowerCase()) {
                row.Role = "organization admin";
            }
        }
        return ({
            RowId: `row-${index}`,
            rowIndex: index,
            FirstName: row["FirstName"],
            LastName: row["LastName"],
            Email: row["Email"],
            Role: row["Role"],
            SeatType: row["SeatType"],
            PrimaryPhoneNumber: row["PhoneNumber"],
            SecondaryPhoneNumbers: row["SecondaryPhoneNumbers"],
            PhoneNumberGroups: row["PhoneNumberGroups"]
        })
    })
}

export const ImportStepCard = ({ children, className = "", isDarkMode }: { children: ReactNode, className?: string, isDarkMode: boolean }) => {
    return (
        <div className={`br-3 p-3 border-primary-divider ${isDarkMode ? "bg-color-primary-background" : "bg-color-secondary"} ${className}`}>
            {children}
        </div>
    )
}

export const ImportColumnChip = ({ label, required, columnLetter }: { label: string, required: boolean, columnLetter?: string }) => {
    const text = required ? `${label} *` : label

    return (
        <Chip
            size="small"
            variant="outlined"
            label={columnLetter ? `${columnLetter} · ${text}` : text}
            className="mr-1 mb-1"
            classes={{ label: "h5-regular color-primary-light" }}
            sx={{
                color: "var(--color-primary-light)",
                borderColor: "var(--color-current-plan-background)",
                "& .MuiChip-label": {
                    fontFamily: "silkamedium !important",
                },
                borderRadius: "999px",
                backgroundColor: "var(--color-current-plan-background)",
            }}
        />
    )
}

export const ImportInfoBanner = ({ title, children }: { title: string, children: ReactNode }) => {
    return (
        <div className='d-flex align-start p-2 br-2 bg-color-new-users-step-card-bg b-around-new-users-step-card-border'>
            <InfoBlueBorderIcon className='flex-shrink-0' />
            <div className='ml-1'>
                <div className='h4-medium color-primary-accent'>{title}</div>
                <div className='h5-regular color-text-field-label'>
                    {children}
                </div>
            </div>
        </div>
    )
}

export const ImportStepFooter = ({
    backLabel,
    onBack,
    continueLabel,
    onContinue,
    canContinue
}: {
    backLabel: string
    onBack: () => void
    continueLabel: string
    onContinue: () => void
    canContinue: boolean
}) => {
    return (
        <div className='d-flex justify-between align-center mt-auto pt-3'>
            <button type="button" className='h4-medium color-primary-accent' onClick={onBack}>
                {backLabel}
            </button>
            <ButtonComponent
                ButtonLabel={continueLabel}
                Variant={canContinue ? 'primary-btn' : 'disabled-btn'}
                ClassName="m-0 pt-2 pb-2 br-2"
                buttonProps={{ style: { textTransform: 'none', fontFamily: 'silkamedium', fontSize: '1rem' } }}
                OnClick={onContinue}
            />
        </div>
    )
}

export const ImportStepLayout = ({
    isDarkMode,
    left,
    right
}: {
    isDarkMode: boolean
    left: ReactNode
    right: ReactNode
}) => {
    return (
        <div className='d-flex w-p-100 mt-2 align-start'>
            <ImportStepCard isDarkMode={isDarkMode} className="w-p-62 mr-2 d-flex flex-direction">
                {left}
            </ImportStepCard>
            <div className='flex-1 d-flex flex-direction'>
                {right}
            </div>
        </div>
    )
}
